import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { CATEGORIES, GENRE_SETS } from "../lib/categories";
import { useCategories } from "../lib/useCategories";
import TrackCard from "../components/TrackCard";
import { Search, Filter } from "lucide-react";
import AdSlot from "../components/AdSlot";

const COUNTRIES = ["Tous", "Madagascar", "Sénégal", "Nigeria", "Côte d'Ivoire", "Cameroun", "RDC", "Mali", "Afrique du Sud", "Kenya", "Ghana", "Algérie"];
const LANGUAGES = ["Toutes", "Malgache", "Français", "Anglais", "Wolof", "Lingala", "Swahili", "Yoruba", "Arabe"];
const TYPES = [{k:"all",l:"Tous"},{k:"audio",l:"Audio"},{k:"video",l:"Vidéo"},{k:"image",l:"Image"}];

export const ExplorePage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { cats: dynamicCats } = useCategories();
  const visibleCats = dynamicCats.filter(c => c.is_active); // les coming-soon ne sont pas filtrables dans Explore
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [category, setCategory] = useState(searchParams.get("category") || "all");
  const [genre, setGenre] = useState("Tous");
  const [country, setCountry] = useState("Tous");
  const [language, setLanguage] = useState("Toutes");
  const [type, setType] = useState("all");

  // Genres dynamiques selon la catégorie active
  const genres = useMemo(() => {
    if (category === "all") {
      // Union de tous les genres uniques
      const all = Array.from(new Set(Object.values(GENRE_SETS).flat()));
      return ["Tous", ...all];
    }
    return ["Tous", ...(GENRE_SETS[category] || [])];
  }, [category]);

  // Reset genre si la catégorie change et que le genre courant n'y est plus
  useEffect(() => {
    if (genre !== "Tous" && !genres.includes(genre)) setGenre("Tous");
  }, [category, genre, genres]);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (category !== "all") params.category = category;
    if (genre !== "Tous") params.genre = genre;
    if (country !== "Tous") params.country = country;
    if (language !== "Toutes") params.language = language;
    if (type !== "all") params.track_type = type;

    if (q) {
      api.get("/search", { params: { q } }).then(({ data }) => {
        setTracks(data.tracks);
      }).finally(() => setLoading(false));
    } else {
      api.get("/tracks", { params }).then(({ data }) => setTracks(data)).finally(() => setLoading(false));
    }
  }, [q, category, genre, country, language, type]);

  return (
    <div className="px-6 md:px-12 py-10 max-w-7xl mx-auto" data-testid="explore-page">
      <p className="text-[10px] tracking-[0.25em] uppercase text-brand-orange font-bold mb-2">Explorer</p>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter mb-8">
        Découvrez les talents.
      </h1>

      <AdSlot placement="explore" category={category && category !== "all" ? category : undefined} className="mb-6" />

      <div className="flex flex-wrap gap-2 mb-8" data-testid="category-filter">
        <button
          onClick={() => { setCategory("all"); setSearchParams({}); }}
          className={`tag-pill ${category === "all" ? "active" : ""}`}
          data-testid="filter-category-all"
        >
          🌍 Toutes
        </button>
        {visibleCats.map((c) => (
          <button
            key={c.key}
            onClick={() => { setCategory(c.key); setSearchParams({ category: c.key }); }}
            className={`tag-pill ${category === c.key ? "active" : ""}`}
            data-testid={`filter-category-${c.key}`}
          >
            {c.emoji} {c.label}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-2 bg-white/[0.04] border border-white/10 rounded-full px-5 py-3 mb-8 max-w-2xl focus-within:border-brand-red/50 transition-colors">
        <Search size={18} className="text-white/40" />
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Rechercher un morceau, un artiste, un genre..."
          className="flex-1 bg-transparent outline-none text-sm"
          data-testid="explore-search-input"
        />
      </div>

      <div className="space-y-4 mb-10">
        <FilterRow label={category === "music" ? "Genre" : category === "humor" ? "Format" : category === "all" ? "Genre" : "Type"} items={genres} value={genre} onChange={setGenre} testid="filter-genre" />
        <FilterRow label="Pays" items={COUNTRIES} value={country} onChange={setCountry} testid="filter-country" />
        <FilterRow label="Langue" items={LANGUAGES} value={language} onChange={setLanguage} testid="filter-language" />
        <div>
          <p className="text-[10px] tracking-[0.25em] uppercase text-white/40 font-bold mb-2">Type de média</p>
          <div className="flex gap-2">
            {TYPES.map((t) => (
              <button
                key={t.k}
                onClick={() => setType(t.k)}
                className={`tag-pill ${type === t.k ? "active" : ""}`}
                data-testid={`filter-type-${t.k}`}
              >
                {t.l}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <p className="text-white/50">Chargement...</p>
      ) : tracks.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-10 text-center">
          <Filter size={32} className="mx-auto text-white/40 mb-3" />
          <p className="text-white/70">Aucun résultat pour ces critères.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 stagger">
          {tracks.map((t) => <TrackCard key={t.track_id} track={t} list={tracks} />)}
        </div>
      )}
    </div>
  );
};

const FilterRow = ({ label, items, value, onChange, testid }) => (
  <div>
    <p className="text-[10px] tracking-[0.25em] uppercase text-white/40 font-bold mb-2">{label}</p>
    <div className="flex flex-wrap gap-2">
      {items.map((i) => (
        <button
          key={i}
          onClick={() => onChange(i)}
          className={`tag-pill ${value === i ? "active" : ""}`}
          data-testid={`${testid}-${i}`}
        >
          {i}
        </button>
      ))}
    </div>
  </div>
);

export default ExplorePage;
